import { Accordion, Icon } from '@scale/scaleui';
import { SearchResult } from '@scale/shared/types/chat';
import { useState, useCallback } from 'react';
import _ from 'lodash';
import useFetchSearchResults from './useFetchSearchResults';
import SearchResultsSection from './SearchResultsSection';

interface AdditionalReferencesAccordionProps {
  setAdditionalReference: (
    referenceIndex: number,
    update: { searchQuery: string; searchQueryResults: SearchResult[] },
  ) => void;
  deleteAdditionalReference: (referenceIndex: number) => void;
  referenceIndex: number;
  existingSearchQuery: string;
  existingSearchQueryResults: SearchResult[];
  readOnly: boolean;
  searchModelId: string;
}

const AdditionalReferencesAccordion: React.FC<AdditionalReferencesAccordionProps> = ({
  setAdditionalReference,
  deleteAdditionalReference,
  referenceIndex,
  existingSearchQuery,
  existingSearchQueryResults,
  readOnly,
  searchModelId,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>(existingSearchQuery);
  const [searchQueryResults, setSearchQueryResults] = useState<SearchResult[]>(
    existingSearchQueryResults,
  );
  const [expanded, setExpanded] = useState<boolean>(
    !_.some(existingSearchQueryResults, ['isChecked', true]),
  );

  const processSearchResults = useCallback(
    (searchTerm: string, searchResults: SearchResult[]) => {
      const newSearchResults = searchResults.map(result => ({
        ...result,
        isChecked: false,
      }));
      setSearchQueryResults(newSearchResults);
      setAdditionalReference(referenceIndex, {
        searchQuery: searchTerm,
        searchQueryResults: newSearchResults,
      });
    },
    [referenceIndex, setAdditionalReference],
  );
  const [searchState, fetchSearchResults] = useFetchSearchResults(processSearchResults);

  return (
    <div className="flex w-full">
      <div className="flex-grow">
        <Accordion
          header={
            searchQuery.length > 0 ? searchQuery : `Additional Reference ${referenceIndex + 1}`
          }
          onChange={() => {
            setExpanded(!expanded);
          }}
          expanded={expanded}
          target={
            <div className="space-y-4">
              <SearchResultsSection
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                readOnly={!!readOnly}
                searchQueryResults={searchQueryResults}
                disableFetchSearchResults={!!readOnly}
                searchState={searchState}
                fetchSearchResults={fetchSearchResults}
                onCheckboxChange={(index: number) => {
                  const updatedResults = _.cloneDeep(searchQueryResults);
                  updatedResults[index].isChecked = !updatedResults[index].isChecked;
                  setSearchQueryResults(updatedResults);
                  setAdditionalReference(referenceIndex, {
                    searchQuery,
                    searchQueryResults: updatedResults,
                  });
                }}
                resultIndex={referenceIndex + 2}
                searchModelId={searchModelId}
              />
            </div>
          }
        />
      </div>
      <div className="flex-shrink-0 flex justify-center items-center ml-2">
        <Icon
          icon={['far', 'trash-alt']}
          size="md"
          variant="neutral"
          onClick={() => {
            deleteAdditionalReference(referenceIndex);
          }}
          disabled={readOnly}
        />
      </div>
    </div>
  );
};

export default AdditionalReferencesAccordion;
